import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';
import axios from 'axios';

import { IVehicleModel, defaultValue } from 'app/shared/model/vehicle-model.model';
import { EntityState, IQueryParams, createEntitySlice, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { cleanEntity } from 'app/shared/util/entity-utils';

const initialState: EntityState<IVehicleModel> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/vehicle-models';
const apiUrlCategory = 'api/vehicle-models/category/';

// Actions

interface IPagedVehicleModelParams extends IQueryParams {
  nameSearch?: string;
}

export const getEntities = createAsyncThunk('vehicleModel/fetch_entity_list', async ({ page, size, sort }: IQueryParams) => {
  const requestUrl = `${apiUrl}${sort ? `?page=${page}&size=${size}&sort=${sort}&` : '?'}cacheBuster=${new Date().getTime()}`;
  return axios.get<IVehicleModel[]>(requestUrl);
});

export const getEntitiesByCategories = createAsyncThunk('vehicleModel/fetch_entity_list', async (category: string) => {
  const requestUrl = `${apiUrlCategory}${category}?cacheBuster=${new Date().getTime()}`;
  return axios.get<IVehicleModel[]>(requestUrl);
});

export const getVehicleModelSummary = createAsyncThunk('vehicleModel/fetch_entity_list', async ({ page, size, sort, nameSearch }: IPagedVehicleModelParams) => {
  const requestUrl = `${apiUrl}?${sort ? `page=${page}&size=${size}&sort=${sort}&nameSearch=${nameSearch}&` : ''}cacheBuster=${new Date().getTime()}`;
  return axios.get<IVehicleModel[]>(requestUrl);
});

export const getEntity = createAsyncThunk(
  'vehicleModel/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IVehicleModel>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  'vehicleModel/create_entity',
  async (entity: IVehicleModel, thunkAPI) => {
    const result = await axios.post<IVehicleModel>(apiUrl, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  'vehicleModel/update_entity',
  async (entity: IVehicleModel, thunkAPI) => {
    const result = await axios.put<IVehicleModel>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  'vehicleModel/partial_update_entity',
  async (entity: IVehicleModel, thunkAPI) => {
    const result = await axios.patch<IVehicleModel>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'vehicleModel/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<IVehicleModel>(requestUrl);
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

// slice

export const VehicleModelSlice = createEntitySlice({
  name: 'vehicleModel',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      })
      .addMatcher(isFulfilled(getEntitiesByCategories), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      });
  },
});

export const { reset } = VehicleModelSlice.actions;

// Reducer
export default VehicleModelSlice.reducer;
